import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Page, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { Badge } from '../../components/shared/Badges';
import { Card, Kpi, KpiLedger } from '../../components/shared/Card';
import { Avatar } from '../../components/shared/People';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { BarChart, HBars, LineChart, useTableToggle } from '../../components/shared/charts';
import { PRESENCE_CLASS, analyticsApi, hourClock, isWeekday, rangeText, shortDate, useAnalytics } from '../../services/analyticsApi';

const ROUTE_COLOR = {
  AutoAttendant: 'var(--route-aa)',
  Queue: 'var(--route-queue)',
  Agent: 'var(--route-agent)',
  Transfer: 'var(--route-transfer)',
  Voicemail: 'var(--route-voicemail)',
  Abandoned: 'var(--route-abandon)',
  Forward: 'var(--route-forward)',
  External: 'var(--route-forward)',
};

const BUCKET_LABELS = ['0–15s', '15–30s', '30–60s', '1–2m', '2–5m', '5m+'];

function subtitle(q, levelName, terms) {
  const a = q.attributedTo;
  if (a.kind === 'Community') return `Teams call queue · Mapped to ${a.name}`;
  if (a.kind === 'Hierarchy') return `Teams call queue · Mapped to the ${a.name} ${levelName} · ${terms.community} identified from each caller`;
  if (a.kind === 'Function') return `Teams call queue · Organization-wide · ${terms.community} identified from each caller`;
  return `Teams call queue · ${terms.community} identified from each caller`;
}

function SlaDailyCard({ data }) {
  const table = useTableToggle();
  const q = data.queue;
  const days = (data.slaDaily || q.daily).filter((d) => d.calls > 0 || isWeekday(d.date));
  const values = days.map((d) => (d.calls ? Number(d.serviceLevelPercent) : null));
  const low = Math.min(50, ...values.filter((v) => v != null).map((v) => Math.floor(v / 10) * 10));
  return (
    <Card className="c-7" title="SLA met per day" description={`Answered within ${q.targets.answerWithinSeconds} seconds, business hours`} actions={table.button}>
      <LineChart
        labels={days.map((d) => shortDate(d.date))}
        xName="Day"
        height={230}
        yMin={low}
        yMax={100}
        yStep={10}
        yFmt={(v) => `${v}%`}
        vFmt={(v) => `${Number(v).toFixed(1)}%`}
        series={[{ name: 'SLA met', values, slot: 1 }]}
        target={{ value: Number(q.targets.answerTargetPercent), label: `Target ${Number(q.targets.answerTargetPercent)}%` }}
        showTable={table.on}
      />
    </Card>
  );
}

function WaitCard({ data }) {
  const table = useTableToggle();
  const buckets = data.queue.waitHistogram;
  return (
    <Card className="c-5" title="How long callers waited" description="Answered and abandoned calls by wait time" actions={table.button}>
      <BarChart
        labels={BUCKET_LABELS.slice(0, buckets.length)}
        xName="Wait"
        height={230}
        stacked
        series={[
          { name: 'Answered', values: buckets.map((b) => b.answered), slot: 1 },
          { name: 'Abandoned', values: buckets.map((b) => b.abandoned), slot: 8 },
        ]}
        showTable={table.on}
      />
    </Card>
  );
}

/** "a" or "an" before a spoken number: an 8, an 11, an 18, an 80…; a 90. */
export function indefiniteArticle(n) {
  const digits = String(Math.round(Math.abs(Number(n)) || 0));
  return digits.startsWith('8') || digits === '11' || digits === '18' ? 'an' : 'a';
}

export function QueueDetailPage() {
  const { id } = useParams();
  const h = useHierarchyFilter();
  const terms = useTerms();
  const navigate = useNavigate();
  const { data, error, retry } = useAnalytics(() => analyticsApi.queue(id, h.params), JSON.stringify([id, h.params]));
  const name = data?.queue.queueName || 'Queue';
  const levelName = h.topLevelName || 'Division';

  let body;
  if (error && error.response?.status === 404) body = <EmptyState icon="layers" title="Queue not found" body="This queue isn't in your organization's Teams setup, or you don't have access to its calls." />;
  else if (error) body = <ErrorState title="Queue couldn't load" body="Vertolytics couldn't reach the analytics service." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={420} />;
  else if (!data.queue.totalCalls)
    body = (
      <EmptyState
        icon="layers"
        title="No calls reached this queue in this period"
        body={`The queue exists in Teams but no calls were routed to it between ${rangeText(h.range.from, h.range.to)}. Check the auto attendant menu options that point here.`}
      />
    );
  else {
    const q = data.queue;
    const target = Number(q.targets.answerTargetPercent);
    const below = q.serviceLevelCalls > 0 && q.serviceLevelPercent < target;
    const perWeekday = q.daily.filter((d) => isWeekday(d.date)).length || 1;
    const attributed = data.attributionSources.reduce((a, b) => a + b.calls, 0) || 1;
    const memberCount = data.memberCount;
    body = (
      <div className="stack-lg">
        {data.daysBelowTarget > 0 && (
          <div className="alert alert-warning">
            <Icon name="alert" />
            <div>
              <div className="a-title">
                Below SLA on {data.daysBelowTarget} of the last {data.daysWithCalls} days
              </div>
              <div>
                {q.serviceLevelPercent.toFixed(1)}% of calls were answered within {q.targets.answerWithinSeconds} seconds against {indefiniteArticle(target)} {target}% target.
                {data.longestWaitHour != null ? ` Waits rise after ${hourClock(data.longestWaitHour)}` : ''}
                {data.longestWaitHour != null && data.shared && memberCount > 0 ? `, when ${data.shared.agents} of ${memberCount} agents are also members of ${data.shared.queueName}.` : data.longestWaitHour != null ? '.' : ''}
              </div>
            </div>
            <div className="a-acts">
              <Link className="btn btn-sm" to={`/heatmap?${new URLSearchParams({ queueIds: id, queueName: q.queueName || '' }).toString()}`}>
                See by hour
              </Link>
            </div>
          </div>
        )}
        <KpiLedger>
          <Kpi label="Offered" value={fmt.int(q.totalCalls)} meta={`${fmt.int(q.totalCalls / perWeekday)} per weekday`} />
          <Kpi
            label="SLA met"
            value={<span style={below ? { color: 'var(--critical)' } : undefined}>{`${q.serviceLevelPercent.toFixed(1)}%`}</span>}
            meta={below ? <Badge tone="critical" icon="alert">{`Below ${target}% target`}</Badge> : <Badge tone="good" icon="check">{`Target ${target}%`}</Badge>}
          />
          <Kpi label="Average wait" value={fmt.mmss(q.averageWaitSeconds)} meta={`longest ${fmt.mmss(q.maxWaitSeconds)}`} />
          <Kpi label="Abandon rate" value={`${q.abandonRate.toFixed(1)}%`} meta={`${fmt.int(q.abandonedCalls)} calls`} />
          <Kpi label="Average handle time" value={fmt.mmss(q.averageHandleSeconds)} meta={`organization ${fmt.mmss(data.organizationAverageHandleSeconds)}`} />
          <Kpi label="Transferred out" value={`${data.transferredOutPercent.toFixed(1)}%`} meta={data.mostCommonTransferTarget ? `mostly to ${data.mostCommonTransferTarget}` : `${fmt.int(q.transferredCalls)} calls`} />
        </KpiLedger>
        <div className="grid">
          <SlaDailyCard data={data} />
          <WaitCard data={data} />
          <Card className="c-4" title="Where calls come from" description="Previous step in the route">
            <HBars labelAbove items={data.sources.map((s) => ({ label: s.name, value: s.calls, color: ROUTE_COLOR[s.type] || 'var(--route-queue)' }))} />
          </Card>
          <Card className="c-4" title="What happened next" description="Outcome after this queue">
            <HBars labelAbove items={data.next.map((s) => ({ label: s.name, value: s.calls, color: ROUTE_COLOR[s.type] || 'var(--route-agent)' }))} />
          </Card>
          <Card
            className="c-4"
            title={terms.communities}
            description={`How each call's ${terms.communityLower} was identified`}
            actions={
              <Link className="btn btn-ghost btn-sm" to="/admin/communities">
                Mappings
              </Link>
            }
          >
            <HBars labelAbove items={data.communities.map((c) => ({ label: c.name, value: c.calls }))} />
            <div className="caption mt-3">
              Identified by: {data.attributionSources.map((s) => `${s.name.charAt(0).toLowerCase()}${s.name.slice(1)} ${Math.round((s.calls / attributed) * 100)}%`).join(', ')} (
              <Icon name="info" className="ico-sm" /> {fmt.int(attributed)} calls)
            </div>
          </Card>
          <Card
            className="c-12"
            flush
            title="Agents in this queue"
            description={data.shared ? `Membership from Teams; ${data.shared.agents} ${data.shared.agents === 1 ? 'agent is' : 'agents are'} also in ${data.shared.queueName}` : 'Membership from Teams'}
          >
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Status</th>
                    <th className="n">Answered here</th>
                    <th className="n">Avg handle time</th>
                    <th className="n">Answer rate</th>
                    <th>Queue memberships</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.map((a) => {
                    const open = () => navigate(`/agents/${encodeURIComponent(a.agentId)}`);
                    return (
                      <tr key={a.agentId} className="clickable" tabIndex={0} onClick={open} onKeyDown={(e) => e.key === 'Enter' && open()}>
                        <td>
                          <div className="row" style={{ gap: 10 }}>
                            <Avatar name={a.agentName} photoId={a.agentId} />
                            <span className="cell-title">{a.agentName}</span>
                          </div>
                        </td>
                        <td>
                          <span className="row small" style={{ gap: 6 }}>
                            <span className={`presence p-${PRESENCE_CLASS[a.presence.status] || 'offline'}`} />
                            {a.presence.label}
                          </span>
                        </td>
                        <td className="n">{fmt.int(a.answeredHere)}</td>
                        <td className="n">{a.answeredHere ? fmt.mmss(a.averageHandleSeconds) : '—'}</td>
                        <td className="n">{a.answeredHere ? `${Number(a.answerRate).toFixed(1)}%` : '—'}</td>
                        <td className="small">{a.queueNames.join(', ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Page
      title={name}
      documentTitle={name}
      subtitle={data ? subtitle(data.queue, levelName, terms) : undefined}
      crumbs={[{ label: 'Queues', to: '/queues' }, { label: name }]}
      exportConfig={{ name, endpoint: '/export/datasets/Calls', params: { queueIds: id, title: `${name} calls` } }}
    >
      {body}
    </Page>
  );
}
